import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check, Inbox, Lock, Package } from "lucide-react";
import { Badge, conditionBadge, productStatusBadge } from "@/components/Badge";
import { useApp, type Card } from "@/context/AppContext";
import { useCardsByIds } from "@/features/useCardsByIds";

export const Route = createFileRoute("/products/pick")({
  component: ProductPickPage,
  validateSearch: (search: Record<string, unknown>) => ({
    productId: typeof search.productId === "string" ? search.productId : "",
  }),
});

function ProductPickPage() {
  const navigate = useNavigate();
  const { productId } = Route.useSearch();
  const { products, storageBins, clearCardLocations } = useApp();

  const product = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId],
  );

  // TNK-155 phase C: scoped server fetch by id list instead of
  // iterating the full AppContext.cards. Phase D drops the eager
  // hydration; this hook continues working via its server fetch.
  const cardIds = useMemo(() => product?.cardIds ?? [], [product]);
  const { cards: packagedCards } = useCardsByIds(cardIds);

  const pickableCards = useMemo(
    () => packagedCards.filter((c) => c.listingStatus !== "Sold"),
    [packagedCards],
  );

  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());

  const pickedCount = pickedIds.size;
  const pickableCount = pickableCards.length;
  const noPickableCards = pickableCount === 0 && packagedCards.length > 0;
  const allPicked =
    packagedCards.length > 0 &&
    (noPickableCards || pickedCount === pickableCount);

  const togglePick = (card: Card) => {
    if (card.listingStatus === "Sold") return;
    setPickedIds((prev) => {
      const next = new Set(prev);
      if (next.has(card.id)) next.delete(card.id);
      else next.add(card.id);
      return next;
    });
  };

  const handleCompletePick = () => {
    if (!allPicked || !product) return;
    clearCardLocations(packagedCards.map((c) => c.id));
    navigate({ to: "/products/$id", params: { id: product.id } });
  };

  const getBinColor = (locationName: string): string | null => {
    if (!locationName) return null;
    return storageBins.find((b) => b.name === locationName)?.color ?? null;
  };

  const progressPct = pickableCount > 0 ? pickedCount / pickableCount : 0;

  if (!product) {
    return (
      <section className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center">
        <p className="text-sm text-muted-foreground">Product not found</p>
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to products
        </Link>
      </section>
    );
  }

  if (product.status === "Sold" || product.status === "Shipped") {
    return (
      <section className="flex h-full flex-col items-center justify-center gap-3 py-12 text-center">
        <Lock className="h-7 w-7 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          This product is already {product.status.toLowerCase()}
        </p>
        <Link
          to="/products/$id"
          params={{ id: product.id }}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to product
        </Link>
      </section>
    );
  }

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/products/$id"
          params={{ id: product.id }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-bold text-foreground">
              {product.name || product.productType}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Badge
                label={product.status}
                variant={productStatusBadge(product.status)}
              />
              <span className="text-xs text-muted-foreground">
                {noPickableCards
                  ? "All cards sold — locations only"
                  : packagedCards.length > pickableCount
                    ? `${pickedCount} / ${pickableCount} pickable`
                    : `${pickedCount} / ${pickableCount} picked`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="h-1 w-full bg-muted">
        <div
          className="h-1 transition-all"
          style={{
            width: `${Math.round(progressPct * 100)}%`,
            backgroundColor: allPicked
              ? "#22c55e"
              : "var(--color-primary, #3b82f6)",
          }}
        />
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {packagedCards.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <Inbox className="h-7 w-7 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No cards in this product
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border bg-card">
            {packagedCards.map((card, idx) => {
              const isPicked = pickedIds.has(card.id);
              const isSold = card.listingStatus === "Sold";
              const binColor = getBinColor(card.storageLocation);
              const isLast = idx === packagedCards.length - 1;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => togglePick(card)}
                  disabled={isSold}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-opacity ${
                    isLast ? "" : "border-b border-border"
                  } ${isPicked ? "opacity-60" : "opacity-100"} ${
                    isSold ? "cursor-default" : "hover:bg-card/80"
                  }`}
                >
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      isSold
                        ? "border-border bg-muted text-muted-foreground"
                        : isPicked
                          ? "border-emerald-500 bg-emerald-500 text-white"
                          : "border-border bg-transparent"
                    }`}
                  >
                    {isSold ? (
                      <Lock className="h-3 w-3" />
                    ) : isPicked ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : null}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div
                      className={`truncate text-sm font-semibold ${
                        isSold ? "text-muted-foreground" : "text-foreground"
                      }`}
                    >
                      {card.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        label={card.condition}
                        variant={conditionBadge(card.condition)}
                      />
                      <span className="text-[11px] text-muted-foreground">
                        {[card.setName, `×${card.quantity}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                  </div>
                  <div className="flex max-w-[7rem] items-center gap-1.5">
                    {card.storageLocation ? (
                      <>
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor:
                              binColor ?? "var(--color-muted-foreground)",
                          }}
                        />
                        <span className="truncate text-[11px] font-medium text-muted-foreground">
                          {card.storageLocation}
                        </span>
                      </>
                    ) : (
                      <span className="text-[11px] text-muted-foreground/60">
                        Unassigned
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-border pt-3">
        <button
          type="button"
          onClick={handleCompletePick}
          disabled={!allPicked}
          className={`flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-bold ${
            allPicked
              ? "bg-emerald-500 text-white hover:opacity-90"
              : "cursor-not-allowed bg-muted text-muted-foreground"
          }`}
        >
          <Package className="h-4 w-4" />
          {noPickableCards
            ? "Clear Locations"
            : allPicked
              ? "Complete Pick"
              : `Pick all ${pickableCount} card${pickableCount === 1 ? "" : "s"} to continue`}
        </button>
      </div>
    </section>
  );
}
